import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SoccerCompetition, SportType } from '@/types';
import { cn } from '@/lib/utils';
import { getCompetitionUrlSlug } from '@/utils/sportUtils';

interface SoccerViewSwitcherProps {
  sport: SportType;
  mode: 'completed' | 'upcoming';
  competitions?: SoccerCompetition[];
  className?: string;
}

function buildCompetitionSearch(sport: SportType, competitions: SoccerCompetition[]): string {
  if (sport !== 'soccer') {
    return '';
  }

  const normalized = competitions
    .filter((competition) => Boolean(competition) && competition !== 'All')
    .map((competition) => getCompetitionUrlSlug(competition))
    .filter(Boolean);

  if (normalized.length === 0) {
    return '';
  }

  const params = new URLSearchParams();
  params.set('competitions', normalized.join(','));
  return `?${params.toString()}`;
}

const SoccerViewSwitcher = ({
  sport,
  mode,
  competitions = [],
  className,
}: SoccerViewSwitcherProps) => {
  const search = buildCompetitionSearch(sport, competitions);
  const completedHref = `/sport/${sport}${search}`;
  const upcomingHref = `/sport/${sport}/upcoming${search}`;

  const getButtonClassName = (isActive: boolean) =>
    cn(
      'h-10 rounded-full border px-4 text-sm font-semibold transition-colors',
      isActive
        ? 'border-indigo-400/40 bg-indigo-500 text-white hover:bg-indigo-500'
        : 'border-white/10 bg-slate-900/70 text-slate-300 hover:border-white/20 hover:bg-slate-800 hover:text-white'
    );

  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 rounded-full border border-white/10 bg-slate-950/70 p-1.5 shadow-[0_12px_36px_rgba(2,6,23,0.2)]',
        className
      )}
    >
      <Button asChild className={getButtonClassName(mode === 'completed')}>
        <Link to={completedHref}>Completed</Link>
      </Button>
      <Button asChild className={getButtonClassName(mode === 'upcoming')}>
        <Link to={upcomingHref}>Upcoming</Link>
      </Button>
    </div>
  );
};

export default SoccerViewSwitcher;
